import { MIRA_PERIOD_ROLLING_12, MiraAvailabilityState, MiraPublishedPeriod, MiraSectionDocument } from "@/data/models/Mira";

/**
 * Geräte je Projekt compared like with like in the Markt (Hans, Track #2610112 X7; decided by Taimoor on
 * 2026-10-28): a client with its own devices (at least one on its finished projects in the period) is set
 * against the clients with own devices, every other client against the HyDry-only clients. A client's own
 * devices put several per project, so one Markt over both kinds compared SVT's 1.6 with a pool that was
 * half own-device projects.
 *
 * IRES Cloud publishes Geräte je Projekt (DEV-003) for every client as before, and beside it the same figure
 * under the id of the client's kind: DEV-017 (own devices) or DEV-018 (HyDry only), the other id not
 * applicable. Each Markt document pools the clients of one kind under each id. The screen keeps showing
 * DEV-003, and this module hands it the Markt of its own kind under DEV-003's id, so every Markt card,
 * caption and timeline reads it unchanged.
 */

/** Geräte je Projekt, the tile the screen shows. */
export const MIRA_DEVICES_PER_PROJECT = "MIRA-DEV-003";

/** Geräte je Projekt under each kind of client, which only choose the Markt (never a tile). */
export const MIRA_DEVICES_PER_PROJECT_BY_KIND = ["MIRA-DEV-017", "MIRA-DEV-018"] as const;

/**
 * The kind's id the client's own period carries, or `null` where it carries neither (a document from before
 * IRES Cloud published them, or a period without a finished project with devices). The first applicable
 * one wins, so a group with members of both kinds counts as one with own devices, as one company would.
 */
export function devicesPerProjectKindId(period: MiraPublishedPeriod | null | undefined): string | null {
  for (const id of MIRA_DEVICES_PER_PROJECT_BY_KIND) {
    const state = period?.metrics[id]?.state;
    if (state !== undefined && state !== MiraAvailabilityState.NOT_APPLICABLE) {
      return id;
    }
  }
  return null;
}

/** The entries with Geräte je Projekt's replaced by the kind's, or removed where the kind has none. */
function replacedBy<T>(entries: Record<string, T>, kindId: string): Record<string, T> {
  const out = { ...entries };
  delete out[MIRA_DEVICES_PER_PROJECT];
  if (entries[kindId] !== undefined) {
    out[MIRA_DEVICES_PER_PROJECT] = entries[kindId];
  }
  return out;
}

/**
 * The Markt document with Geräte je Projekt replaced by the Markt of the client's kind: per period by the
 * kind of that period, the timelines by the kind of the rolling year. A period where the client's kind is
 * unknown keeps the Markt as it is; one where the Markt has no figure of that kind loses Geräte je Projekt,
 * so it is never compared with the other kind. Returns the document unchanged where nothing applies.
 *
 * @param market - a Markt document (`miraMarket/{entity}_devices`), or `null`.
 * @param own - the client's (or group's) own document of the same section, or `null`.
 * @returns the document to compare with.
 */
export function withMarketOfOwnKind(
  market: MiraSectionDocument | null,
  own: MiraSectionDocument | null
): MiraSectionDocument | null {
  if (!market || !own) {
    return market;
  }

  const periods: Record<string, MiraPublishedPeriod> = {};
  for (const [key, period] of Object.entries(market.periods ?? {})) {
    const kindId = devicesPerProjectKindId(own.periods?.[key]);
    if (!kindId || period.metrics[MIRA_DEVICES_PER_PROJECT] === undefined) {
      periods[key] = period;
      continue;
    }
    periods[key] = { ...period, metrics: replacedBy(period.metrics, kindId) };
  }

  const trendKindId = devicesPerProjectKindId(own.periods?.[MIRA_PERIOD_ROLLING_12]);
  const swapped = <T>(series: Record<string, T> | undefined): Record<string, T> | undefined => {
    if (!series || !trendKindId || series[MIRA_DEVICES_PER_PROJECT] === undefined) {
      return series;
    }
    return replacedBy(series, trendKindId);
  };

  // Frozen like every Mira document the repository reads (`frozenMiraDocument`), so Vue leaves it alone.
  return Object.freeze({
    ...market,
    periods,
    trend: swapped(market.trend) ?? market.trend,
    ...(market.trends ? { trends: swapped(market.trends) } : {}),
  });
}
